<template>
    <DashboardLayout>
        <template #header>
            <div class="flex items-center justify-between">
                <h2 class="text-xl font-semibold leading-tight text-gray-800">
                    Incident Reports & Analytics
                </h2>
                <div class="flex items-center space-x-2">
                    <button
                        @click="exportCSV"
                        class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        Export CSV
                    </button>
                    <button
                        @click="exportPDF"
                        class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                        </svg>
                        Export PDF
                    </button>
                </div>
            </div>
        </template>

        <div class="py-6">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <!-- Filters -->
                <div class="bg-white rounded-lg shadow p-6 mb-6">
                    <h3 class="text-lg font-semibold text-gray-900 mb-4">Report Filters</h3>
                    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                            <select
                                v-model="filters.dateRange"
                                @change="applyFilters"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                                <option value="7">Last 7 Days</option>
                                <option value="30">Last 30 Days</option>
                                <option value="90">Last 90 Days</option>
                                <option value="365">Last Year</option>
                                <option value="custom">Custom Range</option>
                            </select>
                        </div>

                        <div v-if="filters.dateRange === 'custom'">
                            <label class="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                            <input
                                v-model="filters.dateFrom"
                                type="date"
                                @change="applyFilters"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>

                        <div v-if="filters.dateRange === 'custom'">
                            <label class="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                            <input
                                v-model="filters.dateTo"
                                type="date"
                                @change="applyFilters"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                            <select
                                v-model="filters.severity"
                                @change="applyFilters"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                                <option value="">All Severities</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                            </select>
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Incident Type</label>
                            <select
                                v-model="filters.typeId"
                                @change="applyFilters"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                                <option value="">All Types</option>
                                <option v-for="type in incidentTypes" :key="type.id" :value="type.id">
                                    {{ type.name }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Overview Stats -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div class="bg-white rounded-lg shadow p-6">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-500">Total Incidents</p>
                                <p class="text-3xl font-bold text-gray-900 mt-2">{{ metrics.totalIncidents }}</p>
                            </div>
                            <div class="bg-blue-100 rounded-full p-3">
                                <svg class="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                            </div>
                        </div>
                        <div class="mt-4 flex items-center text-sm">
                            <span :class="metrics.incidentTrend >= 0 ? 'text-green-600' : 'text-red-600'" class="font-semibold">
                                {{ metrics.incidentTrend >= 0 ? '+' : '' }}{{ metrics.incidentTrend }}%
                            </span>
                            <span class="text-gray-500 ml-2">vs previous period</span>
                        </div>
                    </div>

                    <div class="bg-white rounded-lg shadow p-6">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-500">MTTA (Hours)</p>
                                <p class="text-3xl font-bold text-gray-900 mt-2">{{ metrics.mtta }}</p>
                            </div>
                            <div class="bg-yellow-100 rounded-full p-3">
                                <svg class="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>
                        </div>
                        <div class="mt-4">
                            <div class="text-sm text-gray-500">Mean Time to Acknowledge</div>
                        </div>
                    </div>

                    <div class="bg-white rounded-lg shadow p-6">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-500">MTTR (Hours)</p>
                                <p class="text-3xl font-bold text-gray-900 mt-2">{{ metrics.mttr }}</p>
                            </div>
                            <div class="bg-green-100 rounded-full p-3">
                                <svg class="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>
                        </div>
                        <div class="mt-4">
                            <div class="text-sm text-gray-500">Mean Time to Resolve</div>
                        </div>
                    </div>

                    <div class="bg-white rounded-lg shadow p-6">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-500">SLA Compliance</p>
                                <p class="text-3xl font-bold text-gray-900 mt-2">{{ metrics.slaCompliance }}%</p>
                            </div>
                            <div class="bg-purple-100 rounded-full p-3">
                                <svg class="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                                </svg>
                            </div>
                        </div>
                        <div class="mt-4">
                            <div class="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    class="bg-purple-600 h-2 rounded-full"
                                    :style="{ width: metrics.slaCompliance + '%' }"
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Report Tabs -->
                <div class="bg-white rounded-lg shadow">
                    <div class="border-b border-gray-200">
                        <nav class="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                            <button
                                v-for="tab in tabs"
                                :key="tab.value"
                                @click="activeTab = tab.value"
                                :class="[
                                    activeTab === tab.value
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                                    'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
                                ]"
                            >
                                {{ tab.label }}
                            </button>
                        </nav>
                    </div>

                    <div class="p-6">
                        <!-- Overview Tab -->
                        <div v-if="activeTab === 'overview'">
                            <h3 class="text-lg font-semibold text-gray-900 mb-6">Incident Overview</h3>

                            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                                <!-- Severity Distribution -->
                                <div class="border border-gray-200 rounded-lg p-6">
                                    <h4 class="text-md font-semibold text-gray-900 mb-4">Severity Distribution</h4>
                                    <div class="space-y-3">
                                        <div v-for="severity in severityData" :key="severity.name">
                                            <div class="flex items-center justify-between mb-1">
                                                <span class="text-sm text-gray-700">{{ severity.name }}</span>
                                                <span class="text-sm font-semibold text-gray-900">{{ severity.count }} ({{ severity.percentage }}%)</span>
                                            </div>
                                            <div class="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    :class="severity.colorClass"
                                                    class="h-2 rounded-full"
                                                    :style="{ width: severity.percentage + '%' }"
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Status Distribution -->
                                <div class="border border-gray-200 rounded-lg p-6">
                                    <h4 class="text-md font-semibold text-gray-900 mb-4">Status Distribution</h4>
                                    <div class="space-y-3">
                                        <div v-for="status in statusData" :key="status.name">
                                            <div class="flex items-center justify-between mb-1">
                                                <span class="text-sm text-gray-700">{{ status.name }}</span>
                                                <span class="text-sm font-semibold text-gray-900">{{ status.count }} ({{ status.percentage }}%)</span>
                                            </div>
                                            <div class="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    :class="status.colorClass"
                                                    class="h-2 rounded-full"
                                                    :style="{ width: status.percentage + '%' }"
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Type Distribution -->
                            <div class="border border-gray-200 rounded-lg p-6">
                                <h4 class="text-md font-semibold text-gray-900 mb-4">Incident Type Distribution</h4>
                                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div v-for="type in typeDistribution" :key="type.name" class="text-center">
                                        <div class="text-3xl font-bold text-gray-900">{{ type.count }}</div>
                                        <div class="text-sm text-gray-500 mt-1">{{ type.name }}</div>
                                        <div class="text-xs text-gray-400">{{ type.percentage }}%</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Response Metrics Tab -->
                        <div v-if="activeTab === 'response'">
                            <h3 class="text-lg font-semibold text-gray-900 mb-6">Response Time Metrics</h3>

                            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div class="border border-gray-200 rounded-lg p-6 text-center">
                                    <div class="text-sm font-medium text-gray-500 mb-2">MTTI</div>
                                    <div class="text-4xl font-bold text-blue-600">{{ responseMetrics.mtti }}</div>
                                    <div class="text-sm text-gray-500 mt-2">Mean Time to Identify</div>
                                </div>
                                <div class="border border-gray-200 rounded-lg p-6 text-center">
                                    <div class="text-sm font-medium text-gray-500 mb-2">MTTA</div>
                                    <div class="text-4xl font-bold text-yellow-600">{{ responseMetrics.mtta }}</div>
                                    <div class="text-sm text-gray-500 mt-2">Mean Time to Acknowledge</div>
                                </div>
                                <div class="border border-gray-200 rounded-lg p-6 text-center">
                                    <div class="text-sm font-medium text-gray-500 mb-2">MTTR</div>
                                    <div class="text-4xl font-bold text-green-600">{{ responseMetrics.mttr }}</div>
                                    <div class="text-sm text-gray-500 mt-2">Mean Time to Resolve</div>
                                </div>
                            </div>

                            <!-- Response Time Trends Chart Placeholder -->
                            <div class="border border-gray-200 rounded-lg p-6">
                                <h4 class="text-md font-semibold text-gray-900 mb-4">Response Time Trends</h4>
                                <div class="h-64 flex items-center justify-center bg-gray-50 rounded">
                                    <div class="text-center text-gray-500">
                                        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                                        </svg>
                                        <p class="mt-2">Chart: Response time trends over time</p>
                                        <p class="text-xs mt-1">Integrate with Chart.js or similar library</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Financial Impact Tab -->
                        <div v-if="activeTab === 'financial'">
                            <h3 class="text-lg font-semibold text-gray-900 mb-6">Financial Impact Analysis</h3>

                            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div class="border border-gray-200 rounded-lg p-6">
                                    <div class="text-sm font-medium text-gray-500">Total Financial Impact</div>
                                    <div class="text-3xl font-bold text-gray-900 mt-2">
                                        ${{ financialMetrics.totalImpact.toLocaleString() }}
                                    </div>
                                </div>
                                <div class="border border-gray-200 rounded-lg p-6">
                                    <div class="text-sm font-medium text-gray-500">Average Cost per Incident</div>
                                    <div class="text-3xl font-bold text-gray-900 mt-2">
                                        ${{ financialMetrics.averageCost.toLocaleString() }}
                                    </div>
                                </div>
                                <div class="border border-gray-200 rounded-lg p-6">
                                    <div class="text-sm font-medium text-gray-500">Highest Impact Incident</div>
                                    <div class="text-3xl font-bold text-gray-900 mt-2">
                                        ${{ financialMetrics.highestImpact.toLocaleString() }}
                                    </div>
                                </div>
                            </div>

                            <!-- Cost by Type -->
                            <div class="border border-gray-200 rounded-lg p-6">
                                <h4 class="text-md font-semibold text-gray-900 mb-4">Cost by Incident Type</h4>
                                <div class="space-y-4">
                                    <div v-for="type in costByType" :key="type.name">
                                        <div class="flex items-center justify-between mb-2">
                                            <span class="text-sm font-medium text-gray-700">{{ type.name }}</span>
                                            <span class="text-sm font-semibold text-gray-900">${{ type.cost.toLocaleString() }}</span>
                                        </div>
                                        <div class="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                class="bg-blue-600 h-2 rounded-full"
                                                :style="{ width: (type.cost / financialMetrics.totalImpact * 100) + '%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Investigation Metrics Tab -->
                        <div v-if="activeTab === 'investigation'">
                            <h3 class="text-lg font-semibold text-gray-900 mb-6">Investigation Metrics</h3>

                            <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                                <div class="border border-gray-200 rounded-lg p-6 text-center">
                                    <div class="text-3xl font-bold text-gray-900">{{ investigationMetrics.total }}</div>
                                    <div class="text-sm text-gray-500 mt-2">Total Investigations</div>
                                </div>
                                <div class="border border-gray-200 rounded-lg p-6 text-center">
                                    <div class="text-3xl font-bold text-blue-600">{{ investigationMetrics.inProgress }}</div>
                                    <div class="text-sm text-gray-500 mt-2">In Progress</div>
                                </div>
                                <div class="border border-gray-200 rounded-lg p-6 text-center">
                                    <div class="text-3xl font-bold text-green-600">{{ investigationMetrics.completed }}</div>
                                    <div class="text-sm text-gray-500 mt-2">Completed</div>
                                </div>
                                <div class="border border-gray-200 rounded-lg p-6 text-center">
                                    <div class="text-3xl font-bold text-purple-600">{{ investigationMetrics.avgDuration }}</div>
                                    <div class="text-sm text-gray-500 mt-2">Avg Duration (Days)</div>
                                </div>
                            </div>

                            <!-- Root Causes -->
                            <div class="border border-gray-200 rounded-lg p-6 mb-6">
                                <h4 class="text-md font-semibold text-gray-900 mb-4">Most Common Root Causes</h4>
                                <div class="space-y-3">
                                    <div v-for="(cause, index) in rootCauses" :key="index" class="flex items-center justify-between">
                                        <span class="text-sm text-gray-700">{{ cause.name }}</span>
                                        <span class="text-sm font-semibold text-gray-900">{{ cause.count }} incidents</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Actions Effectiveness -->
                            <div class="border border-gray-200 rounded-lg p-6">
                                <h4 class="text-md font-semibold text-gray-900 mb-4">Actions Effectiveness</h4>
                                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div class="text-center">
                                        <div class="text-2xl font-bold text-green-600">{{ actionsEffectiveness.highly }}%</div>
                                        <div class="text-xs text-gray-500 mt-1">Highly Effective</div>
                                    </div>
                                    <div class="text-center">
                                        <div class="text-2xl font-bold text-blue-600">{{ actionsEffectiveness.effective }}%</div>
                                        <div class="text-xs text-gray-500 mt-1">Effective</div>
                                    </div>
                                    <div class="text-center">
                                        <div class="text-2xl font-bold text-yellow-600">{{ actionsEffectiveness.partial }}%</div>
                                        <div class="text-xs text-gray-500 mt-1">Partially Effective</div>
                                    </div>
                                    <div class="text-center">
                                        <div class="text-2xl font-bold text-red-600">{{ actionsEffectiveness.ineffective }}%</div>
                                        <div class="text-xs text-gray-500 mt-1">Ineffective</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Asset Impact Tab -->
                        <div v-if="activeTab === 'assets'">
                            <h3 class="text-lg font-semibold text-gray-900 mb-6">Asset Impact Analysis</h3>

                            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div class="border border-gray-200 rounded-lg p-6 text-center">
                                    <div class="text-3xl font-bold text-gray-900">{{ assetMetrics.totalAffected }}</div>
                                    <div class="text-sm text-gray-500 mt-2">Total Assets Affected</div>
                                </div>
                                <div class="border border-gray-200 rounded-lg p-6 text-center">
                                    <div class="text-3xl font-bold text-red-600">{{ assetMetrics.totalDowntime }}</div>
                                    <div class="text-sm text-gray-500 mt-2">Total Downtime (Hours)</div>
                                </div>
                                <div class="border border-gray-200 rounded-lg p-6 text-center">
                                    <div class="text-3xl font-bold text-blue-600">{{ assetMetrics.avgRecovery }}</div>
                                    <div class="text-sm text-gray-500 mt-2">Avg Recovery Time (Hours)</div>
                                </div>
                            </div>

                            <!-- Most Affected Assets -->
                            <div class="border border-gray-200 rounded-lg p-6">
                                <h4 class="text-md font-semibold text-gray-900 mb-4">Most Affected Assets</h4>
                                <div class="overflow-x-auto">
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <thead>
                                            <tr>
                                                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Asset</th>
                                                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Incidents</th>
                                                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Downtime</th>
                                                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Impact Level</th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-200">
                                            <tr v-for="asset in mostAffectedAssets" :key="asset.id">
                                                <td class="px-4 py-3 text-sm text-gray-900">{{ asset.name }}</td>
                                                <td class="px-4 py-3 text-sm text-gray-900">{{ asset.incidentCount }}</td>
                                                <td class="px-4 py-3 text-sm text-gray-900">{{ asset.downtime }}h</td>
                                                <td class="px-4 py-3">
                                                    <span
                                                        :class="getImpactClass(asset.avgImpact)"
                                                        class="px-2 py-1 text-xs font-semibold rounded-full"
                                                    >
                                                        {{ asset.avgImpact }}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <!-- Trends Tab -->
                        <div v-if="activeTab === 'trends'">
                            <h3 class="text-lg font-semibold text-gray-900 mb-6">Incident Trends</h3>

                            <!-- Incidents Over Time Chart Placeholder -->
                            <div class="border border-gray-200 rounded-lg p-6 mb-6">
                                <h4 class="text-md font-semibold text-gray-900 mb-4">Incidents Over Time</h4>
                                <div class="h-80 flex items-center justify-center bg-gray-50 rounded">
                                    <div class="text-center text-gray-500">
                                        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"></path>
                                        </svg>
                                        <p class="mt-2">Chart: Line chart showing incident trends</p>
                                        <p class="text-xs mt-1">Integrate with Chart.js or similar library</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Severity Trends -->
                            <div class="border border-gray-200 rounded-lg p-6">
                                <h4 class="text-md font-semibold text-gray-900 mb-4">Severity Trends</h4>
                                <div class="h-80 flex items-center justify-center bg-gray-50 rounded">
                                    <div class="text-center text-gray-500">
                                        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path>
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path>
                                        </svg>
                                        <p class="mt-2">Chart: Stacked area chart by severity</p>
                                        <p class="text-xs mt-1">Integrate with Chart.js or similar library</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </DashboardLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { router } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
    metrics: {
        type: Object,
        required: true,
    },
    severityData: {
        type: Array,
        required: true,
    },
    statusData: {
        type: Array,
        required: true,
    },
    typeDistribution: {
        type: Array,
        required: true,
    },
    responseMetrics: {
        type: Object,
        required: true,
    },
    financialMetrics: {
        type: Object,
        required: true,
    },
    costByType: {
        type: Array,
        required: true,
    },
    investigationMetrics: {
        type: Object,
        required: true,
    },
    rootCauses: {
        type: Array,
        required: true,
    },
    actionsEffectiveness: {
        type: Object,
        required: true,
    },
    assetMetrics: {
        type: Object,
        required: true,
    },
    mostAffectedAssets: {
        type: Array,
        required: true,
    },
    incidentTypes: {
        type: Array,
        required: true,
    },
});

const { showToast } = useToast();

const activeTab = ref('overview');
const tabs = [
    { label: 'Overview', value: 'overview' },
    { label: 'Response Metrics', value: 'response' },
    { label: 'Financial Impact', value: 'financial' },
    { label: 'Investigations', value: 'investigation' },
    { label: 'Asset Impact', value: 'assets' },
    { label: 'Trends', value: 'trends' },
];

const filters = ref({
    dateRange: '30',
    dateFrom: '',
    dateTo: '',
    severity: '',
    typeId: '',
});

const applyFilters = () => {
    router.get(route('incidents.reports'), filters.value, {
        preserveState: true,
        preserveScroll: true,
    });
};

const exportCSV = () => {
    window.location.href = route('incidents.reports.export-csv', filters.value);
    showToast('Exporting report data...', 'info');
};

const exportPDF = () => {
    window.location.href = route('incidents.reports.export-pdf', filters.value);
    showToast('Generating PDF report...', 'info');
};

const getImpactClass = (impact) => {
    const classes = {
        none: 'bg-gray-100 text-gray-800',
        minimal: 'bg-blue-100 text-blue-800',
        moderate: 'bg-yellow-100 text-yellow-800',
        severe: 'bg-orange-100 text-orange-800',
        critical: 'bg-red-100 text-red-800',
    };
    return classes[impact] || 'bg-gray-100 text-gray-800';
};
</script>
